import { Component, Inject, Vue, Watch } from 'vue-property-decorator';
import DirectusService from '@/core/directus/directus.service';
import LanguageCode from '@/locale/language-code';
import LegalTermsWebsiteCMSModel from '@/visa-application/models/LegalTermsWebsiteCMSModel';
import LegalTermsWebsiteContentSlotCMSModel from '@/visa-application/models/LegalTermsWebsiteContentSlotCMSModel';
import { filterDataByCountryAndIssuerAndUniqByTitle, replaceAllOccurrenceOfADetailCode, defineCanonicalTag } from '@/shared/utils/utils';
import { ISSUER_REDIRECT_TOKEN } from '@/constants';
import _ from 'lodash';
import { Keywords } from '@/visa-application/models/KeywordsCMSModel';

@Component
export default class LegalTermsWebsite extends Vue {
  public legalTermsWebsiteCmsData: LegalTermsWebsiteCMSModel = new LegalTermsWebsiteCMSModel();
  public contentSlotData: LegalTermsWebsiteContentSlotCMSModel[] = [];
  public path = [];
  public currentLanguage: LanguageCode = this.$store.getters.currentLanguage;
  public keywords: Partial<Keywords> = {};

  @Inject('directusService')
  private directusService: () => DirectusService;
  public residenceCountryCode: string;
  public issuer: string;
  private unwatchCurrentLanguage: Function;

  public beforeRouteEnter(to, from, next) {
    defineCanonicalTag(to.params.residenceCountryCode, to.params.issuer, to.params.formGroupId, to.fullPath);

    next(vm => {
      vm.residenceCountryCode = to.params.residenceCountryCode;
      vm.issuer = to.params.issuer;
      vm.unwatchCurrentLanguage = vm.$store.watch(
        () => vm.$store.getters.currentLanguage,
        currentLanguage => {
          vm.getContent(currentLanguage);
          vm.getContentSlot(currentLanguage);
        },
        { immediate: true }
      );
    });
  }

  @Watch('$store.getters.currentLanguage', { deep: true })
  public onLanguageChange(language: LanguageCode) {
    this.currentLanguage = language;
    this.getContent(this.currentLanguage);
    this.getContentSlot(this.currentLanguage);
    this.keywords = this.$store.getters.keywords;
  }

  @Watch('$store.getters.keywords', { immediate: true })
  public onKeywordsChanged(keywords: Keywords): void {
    this.keywords = keywords;
  }

  public beforeDestroy(): void {
    this.unwatchCurrentLanguage();
  }

  public getContent(currentLanguage: LanguageCode): void {
    this.directusService()
      .getCollection<LegalTermsWebsiteCMSModel>(
        LegalTermsWebsiteCMSModel,
        'legal_terms_website',
        ['*', 'translation.*'],
        undefined,
        currentLanguage
      )
      .then((response: LegalTermsWebsiteCMSModel[]) => {
        this.legalTermsWebsiteCmsData = response[0];
        this.$store.commit('metaInfo', this.legalTermsWebsiteCmsData);
        this.path['path'] = window.location.pathname;
      })
      .catch(error => console.error(error));
  }

  public getContentSlot(currentLanguage: LanguageCode): void {
    this.directusService()
      .getCollection<LegalTermsWebsiteContentSlotCMSModel>(
        LegalTermsWebsiteContentSlotCMSModel,
        'terms_website_slot',
        ['*', 'translation.*'],
        undefined,
        currentLanguage
      )
      .then((response: LegalTermsWebsiteContentSlotCMSModel[]) => {
        this.contentSlotData = filterDataByCountryAndIssuerAndUniqByTitle(this.issuer, this.residenceCountryCode, response);
        this.contentSlotData.forEach(slotData => {
          slotData.long_desc = replaceAllOccurrenceOfADetailCode(
            slotData.long_desc,
            ISSUER_REDIRECT_TOKEN,
            this.residenceCountryCode + '/' + this.issuer
          );
        });
      })
      .catch(error => console.error(error));
  }

  public redirectAddValueUrl(): void {
    const url = this.legalTermsWebsiteCmsData.action_button_url;
    if (url.startsWith('http')) {
      window.open(url);
    } else if (url.endsWith(`/${this.residenceCountryCode}/${this.issuer}`)) {
      this.$router.push('/' + url);
    } else {
      this.$router.push(`/${url}/${this.residenceCountryCode}/${this.issuer}`);
    }
  }
}
